import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchPaymentSettings,
  savePaymentSettings,
  PAYMENT_SETTINGS_PLACEHOLDER_ID,
  type PaymentSettingsRecord,
} from '../services/paymentSettings';

export default function PaymentSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['operations', 'payment-settings'],
    queryFn: fetchPaymentSettings,
  });

  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setOnlinePaymentsEnabled(data.onlinePaymentsEnabled);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: savePaymentSettings,
    onSuccess: (saved) => {
      setStatusMessage('Ödeme ayarları kaydedildi.');
      queryClient.setQueryData<PaymentSettingsRecord>(
        ['operations', 'payment-settings'],
        saved,
      );
      setOnlinePaymentsEnabled(saved.onlinePaymentsEnabled);
    },
    onError: (err: unknown) => {
      console.error('Failed to save payment settings', err);
      setStatusMessage('Ayarlar kaydedilemedi. Lütfen tekrar deneyin.');
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage(null);

    const persistedId =
      data && data.id !== PAYMENT_SETTINGS_PLACEHOLDER_ID ? data.id : undefined;

    mutation.mutate({
      id: persistedId,
      onlinePaymentsEnabled,
    });
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Ödeme Ayarları</h1>
          <p>Online ödeme yöntemini geçici olarak açıp kapatın.</p>
        </div>
      </header>

      <section className="page-section">
        <div className="card">
          {isLoading ? (
            <p>Ödeme ayarları yükleniyor…</p>
          ) : error ? (
            <p className="text-error">Ayarlar yüklenemedi. Lütfen sayfayı yenileyin.</p>
          ) : (
            <form className="form" onSubmit={handleSubmit}>
              <fieldset className="form-fieldset">
                <legend>Online ödeme erişimi</legend>
                <div className="form-field checkbox">
                  <label htmlFor="online-payments-enabled">
                    <input
                      id="online-payments-enabled"
                      type="checkbox"
                      checked={onlinePaymentsEnabled}
                      onChange={(event) => setOnlinePaymentsEnabled(event.target.checked)}
                    />
                    <span>Online ödeme yöntemini aktif tut</span>
                  </label>
                </div>
                <small>
                  Kapalı olduğunda müşteri uygulamasında online ödeme yöntemi gri görünür, tıklanamaz
                  ve “şu anda kullanılamıyor” notu gösterilir.
                </small>
              </fieldset>

              {statusMessage && (
                <p className={`status-banner ${mutation.isError ? 'error' : 'success'}`}>
                  {statusMessage}
                </p>
              )}

              <button type="submit" className="primary" disabled={mutation.isPending}>
                {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </form>
          )}
        </div>
        {data?.updatedAt && (
          <p>
            Son güncelleme:{' '}
            {new Date(data.updatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
          </p>
        )}
      </section>
    </div>
  );
}
